import React from 'react';
import { Play } from 'lucide-react';
import { featuredVideo } from '../mock';

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// --- Full showcase — mid-page ---

export const FeaturedTrailer = () => {
  if (!featuredVideo) return null;
  const isVertical = featuredVideo.orientation === 'vertical';
  const embedUrl = `https://www.youtube.com/embed/${featuredVideo.videoId}?rel=0&modestbranding=1`;

  return (
    <section
      id="featured-trailer"
      className="py-24 lg:py-36 bg-[#0A0A0B]"
      data-testid="featured-trailer-section"
    >
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Player */}
            <div className="order-2 lg:order-1 flex justify-center">
              <div
                className={`w-full ${isVertical ? 'max-w-[360px] aspect-[9/16]' : 'aspect-video'} bg-black overflow-hidden shadow-2xl shadow-[#14B8A6]/10 border border-[#14B8A6]/20`}
              >
                <iframe
                  src={embedUrl}
                  title={featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="featured-trailer-iframe"
                />
              </div>
            </div>

            {/* Copy */}
            <div className="order-1 lg:order-2">
              <p className="label-sm text-[#14B8A6] mb-4" data-font="label">Featured Trailer</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#F0EDE8] mb-3 leading-tight">
                {featuredVideo.title}
              </h2>
              {featuredVideo.description && (
                <p className="text-lg text-[#E8E4DF]/70 mb-8">{featuredVideo.description}</p>
              )}
              <p className="text-[#E8E4DF] mb-10">
                A cinematic short-form book trailer built to pull readers straight into the world.
                Watch it, share it, and see how the right visuals can turn a scroll into a page-turn.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`https://youtube.com/shorts/${featuredVideo.videoId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#14B8A6] hover:bg-[#0D9488] text-[#0A0A0B] font-medium px-8 py-3.5 text-sm uppercase tracking-[0.12em] transition-all duration-300 hover:shadow-lg hover:shadow-[#14B8A6]/20 inline-flex items-center gap-2"
                  data-testid="featured-trailer-watch-btn"
                >
                  Watch on YouTube
                </a>
                <button
                  onClick={() => scrollToId('work')}
                  className="border-2 border-[#F0EDE8]/50 text-[#F0EDE8] hover:text-[#14B8A6] hover:border-[#14B8A6]/80 hover:bg-[#14B8A6]/5 font-medium px-8 py-3.5 text-sm uppercase tracking-[0.12em] transition-all duration-300"
                  data-testid="featured-trailer-more-btn"
                >
                  See all trailers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Compact teaser — under Hero ---

export const FeaturedTrailerTeaser = () => {
  if (!featuredVideo) return null;

  return (
    <section
      className="py-12 lg:py-16 bg-[#0A0A0B] border-t border-b border-[#3d2a5c]/15"
      data-testid="featured-trailer-teaser"
    >
      <div className="container mx-auto px-6">
        <button
          onClick={() => scrollToId('featured-trailer')}
          className="group max-w-4xl mx-auto w-full flex flex-col sm:flex-row items-center gap-6 sm:gap-8 text-left hover:bg-[#14B8A6]/[0.03] transition-colors p-4 sm:p-6 border border-[#14B8A6]/20 hover:border-[#14B8A6]/50"
          data-testid="featured-trailer-teaser-btn"
        >
          <div className="relative w-24 h-[168px] sm:w-28 sm:h-[196px] flex-shrink-0 overflow-hidden bg-black">
            <img
              src={`https://img.youtube.com/vi/${featuredVideo.videoId}/mqdefault.jpg`}
              alt={`${featuredVideo.title} trailer thumbnail`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
              <div className="w-11 h-11 rounded-full bg-[#14B8A6] flex items-center justify-center shadow-lg">
                <Play className="w-4 h-4 text-[#0A0A0B] ml-0.5 fill-current" />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <p className="label-sm text-[#14B8A6] mb-2" data-font="label">New — Featured Trailer</p>
            <h3 className="text-xl sm:text-2xl font-medium text-[#F0EDE8] group-hover:text-[#14B8A6] transition-colors">
              {featuredVideo.title}
            </h3>
            <p className="text-[#E8E4DF]/70 mt-1">{featuredVideo.description}</p>
          </div>
          <span
            className="hidden sm:inline-block text-xs uppercase tracking-[0.15em] text-[#14B8A6] flex-shrink-0"
            data-font="label"
          >
            Play →
          </span>
        </button>
      </div>
    </section>
  );
};
